// How a tab lays out its cells beyond their sizes (Fixes 9, H2b), as Google Sheets and Excel do it:
//
// - rows and columns hidden from view (SheetTabData.hideRows by row number, .hideCols by letters): they take no room
//   on screen, and their cells read, write and work out as ever. Hiding never deletes anything;
// - blocks of cells merged into one (SheetTabData.merges, "A1:D1"): the block shows its top left cell's value across
//   it, and a click anywhere in it selects the whole block. A merge never overlaps another;
// - borders, a cell's own (CellFmt.bd, the sides it has, "trbl"; bk the sides drawn thick), so they move with the cell when
//   rows are sorted, put in or taken out. Borders are drawn on a cell's edges; a block's choice (all, outer, inner,
//   one side) becomes each cell's sides here.
//
// The browser (src/boards/sheet/), an agent's write_sheet (agent.ts) and the Excel file (io.ts) share these.

import { MAX_COLS, MAX_ROWS, colIndex, colName, parseRect, rectName, type Rect } from "./addr";

/** Merged blocks on a tab, and the cells one may cover. */
export const MAX_MERGES = 500;
export const MAX_MERGE_CELLS = 20_000;

// ---------------------------------------------------------------------------
// hidden rows and columns

/** A tab's hidden rows (by number from 1) or columns (by letters), each sound; undefined for none. */
export function cleanHidden(raw: unknown, axis: "row" | "col"): Record<string, 1> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Record<string, 1> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!v) continue;
    if (axis === "row" ? /^[1-9][0-9]{0,4}$/.test(k) && Number(k) <= MAX_ROWS : colIndex(k) >= 0 && colIndex(k) < MAX_COLS && k === k.toUpperCase()) out[k] = 1;
  }
  return Object.keys(out).length ? out : undefined;
}

/** Whether a row (from 0) or column is hidden on a tab. */
export const rowHidden = (t: { hideRows?: Record<string, 1> }, r: number) => !!t.hideRows?.[String(r + 1)];
export const colHidden = (t: { hideCols?: Record<string, 1> }, c: number) => !!t.hideCols?.[colName(c)];

/** Hidden lines as runs, in words: "D", "F to H", "5 to 7". */
export function hiddenRuns(map: Record<string, 1> | undefined, axis: "row" | "col"): string[] {
  const idx = Object.keys(map ?? {})
    .map((k) => (axis === "row" ? Number(k) - 1 : colIndex(k)))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b);
  const name = (i: number) => (axis === "row" ? String(i + 1) : colName(i));
  const out: string[] = [];
  for (let i = 0; i < idx.length; ) {
    let j = i;
    while (j + 1 < idx.length && idx[j + 1] === idx[j] + 1) j++;
    out.push(i === j ? name(idx[i]) : `${name(idx[i])} to ${name(idx[j])}`);
    i = j + 1;
  }
  return out;
}

/** A tab's hidden map with lines from..to (from 0) hidden or shown again; null when none stay hidden. */
export function withHidden(map: Record<string, 1> | undefined, axis: "row" | "col", from: number, to: number, hide: boolean): Record<string, 1> | null {
  const out: Record<string, 1> = { ...(map ?? {}) };
  for (let i = Math.max(0, from); i <= Math.min(to, (axis === "row" ? MAX_ROWS : MAX_COLS) - 1); i++) {
    const k = axis === "row" ? String(i + 1) : colName(i);
    if (hide) out[k] = 1;
    else delete out[k];
  }
  return Object.keys(out).length ? out : null;
}

/** Lines given as an agent writes them, "D", "D:F", "5", "5:7", to an axis and a span; null for neither. */
export function linesRef(raw: unknown): { axis: "row" | "col"; from: number; to: number } | null {
  const s = typeof raw === "number" ? String(raw) : typeof raw === "string" ? raw.trim().replace(/\$/g, "").toUpperCase() : "";
  const [a, b = a, ...more] = s.split(":");
  if (more.length || !a) return null;
  if (/^\d+$/.test(a) && /^\d+$/.test(b)) {
    const x = Number(a) - 1;
    const y = Number(b) - 1;
    if (x < 0 || y < 0 || x >= MAX_ROWS || y >= MAX_ROWS) return null;
    return { axis: "row", from: Math.min(x, y), to: Math.max(x, y) };
  }
  const x = colIndex(a);
  const y = colIndex(b);
  if (x < 0 || y < 0 || x >= MAX_COLS || y >= MAX_COLS) return null;
  return { axis: "col", from: Math.min(x, y), to: Math.max(x, y) };
}

// ---------------------------------------------------------------------------
// merged cells

/** A merged block's range as kept ("A1:D1"): two cells or more, never an open range; null when it is not one. */
export function mergeRect(range: string): Rect | null {
  if (typeof range !== "string" || !/^[A-Z]{1,3}\d{1,5}:[A-Z]{1,3}\d{1,5}$/.test(range)) return null;
  const x = parseRect(range);
  return x && (x.r1 > x.r0 || x.c1 > x.c0) ? x : null;
}

const meet = (a: Rect, b: Rect) => a.r0 <= b.r1 && b.r0 <= a.r1 && a.c0 <= b.c1 && b.c0 <= a.c1;

/** A tab's merges, each sound and none on another (the first kept); undefined for none. */
export function cleanMerges(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const kept: Rect[] = [];
  const out: string[] = [];
  for (const v of raw) {
    if (out.length >= MAX_MERGES) break;
    const x = typeof v === "string" ? mergeRect(v.trim().replace(/\$/g, "").toUpperCase()) : null;
    if (!x || (x.r1 - x.r0 + 1) * (x.c1 - x.c0 + 1) > MAX_MERGE_CELLS || kept.some((k) => meet(k, x))) continue;
    kept.push(x);
    out.push(rectName(x));
  }
  return out.length ? out : undefined;
}

/** A tab's merges, found by row: the blocks that cover each row. */
export interface MergeIndex {
  all: Rect[];
  byRow: Map<number, Rect[]>;
}

const INDEX = new WeakMap<string[], MergeIndex>();
const NONE: MergeIndex = { all: [], byRow: new Map() };

/** The tab's merges by row, worked out once for each list. */
export function mergeIndex(merges: string[] | undefined): MergeIndex {
  if (!merges?.length) return NONE;
  const kept = INDEX.get(merges);
  if (kept) return kept;
  const all = merges.map(mergeRect).filter((x): x is Rect => !!x);
  const byRow = new Map<number, Rect[]>();
  for (const x of all) for (let r = x.r0; r <= x.r1; r++) byRow.set(r, [...(byRow.get(r) ?? []), x]);
  const out = { all, byRow };
  INDEX.set(merges, out);
  return out;
}

/** The merged block a cell is in, or null. */
export function mergeAt(m: MergeIndex, r: number, c: number): Rect | null {
  const list = m.byRow.get(r);
  if (!list) return null;
  for (const x of list) if (c >= x.c0 && c <= x.c1) return x;
  return null;
}

/** A block grown until every merge it touches is wholly inside it, as a selection grows in Google Sheets. */
export function grownRect(m: MergeIndex, x: Rect): Rect {
  if (!m.all.length) return x;
  let out = { ...x };
  for (let k = 0; k < 20; k++) {
    let hit = false;
    for (const y of m.all)
      if (meet(y, out) && (y.r0 < out.r0 || y.c0 < out.c0 || y.r1 > out.r1 || y.c1 > out.c1)) {
        out = { r0: Math.min(out.r0, y.r0), c0: Math.min(out.c0, y.c0), r1: Math.max(out.r1, y.r1), c1: Math.max(out.c1, y.c1) };
        hit = true;
      }
    if (!hit) break;
  }
  return out;
}

/** How a block is merged: as one, each row as one (horizontally), or each column as one (vertically). */
export type MergeHow = "all" | "rows" | "cols";

/** The blocks a merge of a range makes: one, or one a row or a column (each of two cells or more). */
export function mergeBlocks(x: Rect, how: MergeHow): Rect[] {
  if (how === "rows") return x.c1 > x.c0 ? Array.from({ length: x.r1 - x.r0 + 1 }, (_, i) => ({ r0: x.r0 + i, c0: x.c0, r1: x.r0 + i, c1: x.c1 })) : [];
  if (how === "cols") return x.r1 > x.r0 ? Array.from({ length: x.c1 - x.c0 + 1 }, (_, j) => ({ r0: x.r0, c0: x.c0 + j, r1: x.r1, c1: x.c0 + j })) : [];
  return x.r1 > x.r0 || x.c1 > x.c0 ? [x] : [];
}

/**
 * A tab's merges with blocks merged: those they touch give way. Returns the new list, or words saying why not (a
 * block too big, too many merges).
 */
export function withMerges(merges: string[] | undefined, blocks: Rect[]): string[] | string {
  if (!blocks.length) return "Select two cells or more to merge.";
  for (const b of blocks) if ((b.r1 - b.r0 + 1) * (b.c1 - b.c0 + 1) > MAX_MERGE_CELLS) return `A merged block holds up to ${MAX_MERGE_CELLS.toLocaleString("en-US")} cells.`;
  const rest = (merges ?? []).filter((s) => {
    const y = mergeRect(s);
    return y && !blocks.some((b) => meet(b, y));
  });
  const out = [...rest, ...blocks.map(rectName)];
  if (out.length > MAX_MERGES) return `A tab holds up to ${MAX_MERGES} merged blocks.`;
  return out;
}

/** A tab's merges with every one touching a block taken apart; the list, and how many went. */
export function withoutMerges(merges: string[] | undefined, x: Rect): { merges: string[]; gone: string[] } {
  const keep: string[] = [];
  const gone: string[] = [];
  for (const s of merges ?? []) {
    const y = mergeRect(s);
    if (!y) continue;
    if (meet(x, y)) gone.push(s);
    else keep.push(s);
  }
  return { merges: keep, gone };
}

/** The cells, other than each block's top left one, that hold something: what a merge would hide. */
export function coveredFilled(blocks: Rect[], filled: (r: number, c: number) => boolean): { r: number; c: number }[] {
  const out: { r: number; c: number }[] = [];
  for (const b of blocks)
    for (let r = b.r0; r <= b.r1; r++)
      for (let c = b.c0; c <= b.c1; c++) {
        if (r === b.r0 && c === b.c0) continue;
        if (filled(r, c)) out.push({ r, c });
      }
  return out;
}

/** The first merge in a block, or null: a merge stays where it is, so rows holding one are not sorted. */
export function mergeAcrossRows(m: MergeIndex, x: Rect): Rect | null {
  for (const y of m.all) if (meet(x, y)) return y;
  return null;
}

// ---------------------------------------------------------------------------
// borders

/** The choices a block's borders are set by, as in Google Sheets' Borders. */
export const BORDER_MODES = ["all", "inner", "horizontal", "vertical", "outer", "left", "top", "right", "bottom", "none"] as const;
export type BorderMode = (typeof BORDER_MODES)[number];

export const BORDER_WORDS: Record<BorderMode, string> = {
  all: "All borders",
  inner: "Inner borders",
  horizontal: "Horizontal borders",
  vertical: "Vertical borders",
  outer: "Outer borders",
  left: "Left border",
  top: "Top border",
  right: "Right border",
  bottom: "Bottom border",
  none: "Clear borders",
};

/** A cell's sides as kept: some of t, r, b and l, in that order; "" for none. */
export function cleanSides(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return ["t", "r", "b", "l"].filter((k) => raw.includes(k)).join("");
}

/**
 * Each cell's sides after a block's borders are set, by the cell's place in the block: the sides the choice gives it
 * (added to those it has; "none" takes them all off), and the cells just outside the block whose side faces it, which
 * "none" and a side's own choice clear, so a border cleared never lingers on the cell next door. sides(r, c) is what a
 * cell has now.
 */
export function borderChanges(x: Rect, mode: BorderMode, sides: (r: number, c: number) => string): { r: number; c: number; sides: string; add: string }[] {
  const out = new Map<number, { r: number; c: number; sides: string; add: string }>();
  const set = (r: number, c: number, add: string, take = "") => {
    if (r < 0 || c < 0 || r >= MAX_ROWS || c >= MAX_COLS) return;
    const k = r * 1024 + c;
    const was = out.get(k)?.sides ?? sides(r, c);
    let next = was;
    for (const ch of take) next = next.replace(ch, "");
    next = cleanSides(next + add);
    // a side set again takes this choice's line (thin or thick), so it counts as set even when it was there
    if (next !== was || out.has(k) || add) out.set(k, { r, c, sides: next, add: cleanSides((out.get(k)?.add ?? "") + add) });
  };
  for (let r = x.r0; r <= x.r1; r++)
    for (let c = x.c0; c <= x.c1; c++) {
      const top = r === x.r0;
      const bottom = r === x.r1;
      const left = c === x.c0;
      const right = c === x.c1;
      let add = "";
      switch (mode) {
        case "all":
          add = "trbl";
          break;
        case "outer":
          add = (top ? "t" : "") + (right ? "r" : "") + (bottom ? "b" : "") + (left ? "l" : "");
          break;
        case "inner":
          add = (top ? "" : "t") + (right ? "" : "r") + (bottom ? "" : "b") + (left ? "" : "l");
          break;
        case "horizontal":
          add = (top ? "" : "t") + (bottom ? "" : "b");
          break;
        case "vertical":
          add = (right ? "" : "r") + (left ? "" : "l");
          break;
        case "top":
          add = top ? "t" : "";
          break;
        case "bottom":
          add = bottom ? "b" : "";
          break;
        case "left":
          add = left ? "l" : "";
          break;
        case "right":
          add = right ? "r" : "";
          break;
      }
      if (mode === "none") set(r, c, "", "trbl");
      else if (add) set(r, c, add);
    }
  // the cells next door: a clear takes off their side facing the block
  if (mode === "none") {
    for (let c = x.c0; c <= x.c1; c++) {
      set(x.r0 - 1, c, "", "b");
      set(x.r1 + 1, c, "", "t");
    }
    for (let r = x.r0; r <= x.r1; r++) {
      set(r, x.c0 - 1, "", "r");
      set(r, x.c1 + 1, "", "l");
    }
  }
  return [...out.values()];
}

/**
 * A cell's border look after a change: its sides, and which are thick (the sides this choice set take its line; the
 * others keep theirs). Fields left out when there are none.
 */
export function borderLook(was: { bd?: string; bk?: string }, ch: { sides: string; add: string }, thick: boolean): { bd?: string; bk?: string } {
  if (!ch.sides) return {};
  let bk = cleanSides(was.bk ?? "");
  for (const k of ch.add) bk = bk.replace(k, "");
  bk = cleanSides(bk + (thick ? ch.add : ""));
  bk = ["t", "r", "b", "l"].filter((k) => ch.sides.includes(k) && bk.includes(k)).join("");
  return { bd: ch.sides, ...(bk ? { bk } : {}) };
}
